import { t } from '../i18n.js';

/**
 * Загрузчик локации: поплавок идёт по леске от 0 до 100%.
 *
 * ⚠️ ДОЛЯ ЧЕСТНАЯ, А НЕ ПО ТАЙМЕРУ. Прогресс складывается из настоящих
 * событий: скачался движок, собралась сцена, пришла локация, построились
 * чанки. Последнее и есть основная часть — там сотня чанков, и её доля
 * считается по числу готовых. Врать здесь нельзя: полоска, добежавшая до
 * ста и замершая, хуже честной, ползущей медленно.
 *
 * ⚠️ ЖИВЁТ БЕЗ ДВИЖКА. Этот файл показывается ДО того, как скачан Babylon, —
 * в том и смысл. Поэтому здесь только DOM и стили, никаких сторонних вещей.
 */

/**
 * Два облика загрузчика: вход на базу и выход на рыбалку.
 *
 * ⚠️ ДОЛИ РАЗНЫЕ, ПОТОМУ ЧТО РАЗНОЕ И ЖДЁТ. На базу заходят за полсекунды —
 * проверить ключ да собрать данные. На рыбалку уходят на несколько секунд:
 * восемь мегабайт движка, сборка сцены, чтение локации и сотня чанков берега.
 * Одна таблица на оба случая врала бы: полоска либо прыгала бы до ста за миг,
 * либо ползла бы четверть пути и замирала.
 */
const ОБЛИКИ = {
  база: { ключ: 0.3, база: 0.7 },
  /*
   * ⚠️ ПОЧТИ ПОЛОВИНА ВЕСА ОТДАНА СКАЧИВАНИЮ, И ЭТО ЧЕСТНО. На медленном
   * канале именно оно и занимает всё время: пятьдесят мегабайт снасти,
   * рыболова и земли плюс восемь самого движка. Прежние доли складывались
   * из «сколько шагов», а не «сколько ждать», и полоска стояла на двух
   * процентах всю самую долгую часть пути.
   */
  рыбалка: { движок: 0.45, сцена: 0.1, локация: 0.05, берег: 0.28, мир: 0.12 },
} as const;

export type Облик = keyof typeof ОБЛИКИ;
export type Этап = keyof (typeof ОБЛИКИ)['база'] | keyof (typeof ОБЛИКИ)['рыбалка'];

const ПОДПИСЬ: Record<Этап, string> = {
  ключ: 'Проверяю билет',
  база: 'Открываю журнал',
  движок: 'Разматываю катушку',
  сцена: 'Собираю снасть',
  локация: 'Иду к воде',
  берег: 'Выбираю место',
  мир: 'Осматриваюсь',
};

const СТИЛЬ = `
#if-loader {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 26px;
  background:
    radial-gradient(120% 90% at 50% 15%, rgba(28, 52, 66, 0.72), rgba(8, 14, 20, 0.96) 70%),
    #070c11;
  color: #dfe9ef;
  font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
  transition: opacity 0.45s ease;
}
#if-loader.if-gone { opacity: 0; pointer-events: none; }

#if-loader .if-title {
  font-size: 30px;
  font-weight: 800;
  letter-spacing: 0.22em;
  color: #eaf3f7;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.55);
}

#if-loader .if-bytes {
    margin-top: 6px;
    font-size: 12px;
    letter-spacing: 0.04em;
    color: rgba(223, 232, 238, 0.45);
    min-height: 16px;
  }
  #if-loader .if-stage {
  font-size: 15px;
  letter-spacing: 0.04em;
  color: #9fb6c2;
  min-height: 20px;
}

/* Леска: тонкая нить, по которой идёт поплавок. */
#if-loader .if-line {
  position: relative;
  width: min(560px, 74vw);
  height: 46px;
}
#if-loader .if-line::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 2px;
  border-radius: 2px;
  background: linear-gradient(90deg, rgba(143, 214, 164, 0.16), rgba(143, 214, 164, 0.34));
}
#if-loader .if-done {
  position: absolute;
  left: 0;
  top: 50%;
  height: 2px;
  border-radius: 2px;
  background: linear-gradient(90deg, #6fbf8b, #a8e6bd);
  box-shadow: 0 0 12px rgba(143, 214, 164, 0.55);
}

/* Поплавок: 3D-модель из CSS. Антенна, тело с бликами и киль. */
#if-loader .if-float {
  position: absolute;
  top: 50%;
  width: 14px;
  height: 62px;
  margin-left: -7px;
  transform: translateY(-70%);
  animation: if-bob 2.4s ease-in-out infinite;
  filter: drop-shadow(0 4px 5px rgba(0, 0, 0, 0.4));
}
#if-loader .if-float i {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: block;
}
#if-loader .if-float .if-antenna {
  top: 0;
  width: 2.5px;
  height: 18px;
  background: linear-gradient(90deg, #ff8a65, #e64a19, #bf360c);
  border-radius: 1.5px;
  box-shadow: inset -1px 0 1px rgba(0,0,0,0.4), inset 1px 0 1px rgba(255,255,255,0.6);
}
#if-loader .if-float .if-head {
  top: 16px;
  width: 14px;
  height: 16px;
  border-radius: 50% 50% 15% 15%;
  background: radial-gradient(circle at 35% 35%, #ff5252, #c62828 65%, #8e0000);
  box-shadow: inset -2px -2px 4px rgba(0,0,0,0.5), inset 2px 2px 4px rgba(255,255,255,0.5);
  z-index: 2;
}
#if-loader .if-float .if-stripe {
  top: 31px;
  width: 14px;
  height: 2px;
  background: linear-gradient(90deg, #424242, #111, #000);
  z-index: 3;
}
#if-loader .if-float .if-body {
  top: 32px;
  width: 12px;
  height: 18px;
  border-radius: 15% 15% 50% 50%;
  background: radial-gradient(circle at 35% 35%, #ffffff, #cfd8dc 65%, #90a4ae);
  box-shadow: inset -2px -2px 4px rgba(0,0,0,0.4), inset 2px 2px 4px rgba(255,255,255,0.9);
  z-index: 2;
}
#if-loader .if-float .if-keel {
  top: 48px;
  width: 2px;
  height: 14px;
  background: linear-gradient(90deg, #546e7a, #263238, #111111);
  border-radius: 1px;
}

/* Круги по воде под поплавком — чтобы было видно, что дело живое. */
#if-loader .if-ring {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  margin-left: -6px;
  border: 1px solid rgba(168, 230, 189, 0.5);
  border-radius: 50%;
  transform: translateY(-8%);
  animation: if-ripple 2.4s ease-out infinite;
}
#if-loader .if-ring.if-late { animation-delay: 1.2s; }

@keyframes if-bob {
  0%, 100% { transform: translateY(-70%); }
  50% { transform: translateY(-58%); }
}
@keyframes if-ripple {
  0% { opacity: 0.55; width: 12px; height: 12px; margin-left: -6px; }
  100% { opacity: 0; width: 62px; height: 62px; margin-left: -31px; }
}

#if-loader .if-pct {
  font-size: 44px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #a8e6bd;
  text-shadow: 0 0 26px rgba(143, 214, 164, 0.28);
}
#if-loader .if-pct span { font-size: 20px; opacity: 0.6; margin-left: 3px; }

#if-loader .if-stage.if-bad { color: #ff8a80; }
#if-loader .if-back {
  width: auto;
  margin: 0;
  padding: 8px 20px;
  font: inherit;
  font-size: 13px;
  color: #dfe9ef;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(223, 232, 238, 0.28);
  border-radius: 8px;
  cursor: pointer;
}
#if-loader .if-back:hover { border-color: #a8e6bd; color: #a8e6bd; }

@media (prefers-reduced-motion: reduce) {
  #if-loader .if-float, #if-loader .if-ring { animation: none; }
}
`;

let корень: HTMLElement | null = null;
let полоса: HTMLElement | null = null;
let поплавок: HTMLElement | null = null;
let кругА: HTMLElement | null = null;
let кругБ: HTMLElement | null = null;
let числоЭл: HTMLElement | null = null;
let этапЭл: HTMLElement | null = null;
let байтыЭл: HTMLElement | null = null;
let adminLogЭл: HTMLElement | null = null;
let _isAdmin = false;

export function setAdmin(admin: boolean): void {
  _isAdmin = admin;
  if (_isAdmin && adminLogЭл !== null) adminLogЭл.style.display = 'block';
}

export function logAdmin(msg: string): void {
  if (!_isAdmin || adminLogЭл === null) return;
  const line = document.createElement('div');
  line.textContent = msg;
  adminLogЭл.appendChild(line);
  while (adminLogЭл.children.length > 8) {
    adminLogЭл.firstChild?.remove();
  }
}

/** Сколько уже пройдено по каждому этапу, 0..1. */
const пройдено: Record<string, number> = {};

/** Какой облик показан сейчас. */
let облик: Облик = 'база';

/**
 * Номер показа. Растёт на каждом `показать`.
 *
 * ⚠️ ЗАВЕДЁН ПРОТИВ ПРОТЕКАНИЯ СОСТОЯНИЯ МЕЖДУ ПОКАЗАМИ, И ЭТО НЕ ЗАПАС НА
 * БУДУЩЕЕ, А ЛЕЧЕНИЕ ЗАМЕРЕННОГО БАГА. Флаг «просили убрать» взводился на
 * входе в базу (boot.ts) и оставался взведённым навсегда: `показать` чистил
 * только `пройдено`. Дальше на рыбалке первое же обещание из `держать` —
 * а это модели снасти — заставало флаг взведённым, `держим` пустело, и
 * загрузчик уходил с экрана, когда из сотни чанков берега не было построено
 * ни одного. Приборы при этом поднимались по `готовность()`, то есть игрок
 * получал ровно то, ради чего всё это и заводилось: приборы поверх пустоты.
 *
 * Замерено стендом: с заходом на базу — 100 % и приборы подняты, без захода —
 * 33 % и приборы на месте. Разница была только в этом флаге.
 */
let поколение = 0;

function веса(): Record<string, number> {
  return ОБЛИКИ[облик] as unknown as Record<string, number>;
}

function доля(): number {
  let s = 0;
  const w = веса();
  for (const к of Object.keys(w)) s += (пройдено[к] ?? 0) * w[к];
  return Math.max(0, Math.min(1, s));
}

/*
 * ── Показ отдельно от правды ──────────────────────────────────────────────
 *
 * ⚠️ ДВА ЧИСЛА ВМЕСТО ОДНОГО, И БЕЗ ЭТОГО ПЛАВНОСТИ НЕ БЫВАЕТ. `цель` — та
 * самая честная доля, что складывается из настоящих событий. `показано` —
 * то, что нарисовано на экране; оно догоняет цель и никогда её не обгоняет
 * больше, чем на половину незавершённого этапа.
 *
 * Нужно это затем, что события приходят рывками и с провалами: скачивание
 * main.js не сообщает о себе ничего до самого конца, `scene.whenReadyAsync`
 * молчит по десять секунд. Раньше полоса в такие промежутки просто стояла, и
 * человек читал это как зависание — а между рывками прыгала на двадцать
 * процентов разом (замерено стендом: наибольший скачок 21 %).
 *
 * ⚠️ ПОЛЗОК НЕ ВРЁТ. Он затухающий и упирается в потолок, считаемый от
 * НЕЗАВЕРШЁННОГО этапа: обогнать правду больше чем на половину его остатка
 * полоса не может, а дойти до ста — тем более. Сто ставит только уход.
 *
 * ⚠️ ЧИСЛО И ПОЛОСА ЧИТАЮТ ОДНО И ТО ЖЕ. Прежде проценты присваивались
 * мгновенно, а полоса ехала переходом CSS на 0.35 с — они всё время спорили
 * друг с другом, и вдобавок переход перезапускался по шестьдесят раз в
 * секунду, не успевая доиграть ни разу. Переходы убраны, ведёт кадровый цикл.
 */

/** Честная доля, 0..1. Не убывает. */
let цель = 0;
/** Нарисованная доля, 0..1. Не убывает. */
let показано = 0;
/** Когда цель в последний раз сдвинулась, мс. */
let сдвигБыл = 0;
/** Заявка на кадр — чтобы цикл не остался крутиться после ухода. */
let кадр = 0;

/** Постоянная времени догона, мс. */
const ДОГОН_МС = 130;
/** Постоянная времени ползка на застое, мс. Нарочно медленная. */
const ПОЛЗОК_МС = 5200;
/** Столько ждём без движения цели, прежде чем поползти, мс. */
const ЗАСТОЙ_МС = 220;
/** Выше этого показ не поднимается сам: сто ставит только уход. */
const ПОТОЛОК = 0.99;
/** Сколько полосе дано добежать до ста перед затуханием, мс. */
const ДОБЕГ_МС = 340;

/** Идёт прощание: только теперь полосе позволено дойти до ста. */
let финиш = false;

/**
 * Часы показа.
 *
 * ⚠️ ОДНИ ЧАСЫ НА КАДРОВЫЙ ЦИКЛ И НА СТЕНД, И БЕЗ ЭТОГО СТЕНД МЕРИТ НЕ ТО.
 * Стенд крутит показ вызовами по кусочку — двести миллисекунд, ещё двести. Бери
 * каждый такой вызов время у `performance.now()` заново, и выдуманное время
 * начиналось бы с нуля на каждом кусочке: порог застоя (ЗАСТОЙ_МС) не
 * набирался бы НИКОГДА, а значит ползок не включался бы ни разу — и стенд
 * показывал бы ровный ноль при исправном коде. Ровно это и вышло при первой
 * пробе.
 *
 * В самой игре добавка остаётся нулём: часы идут по `performance.now()`.
 */
let добавкаЧасов = 0;

function сейчасМс(): number {
  return performance.now() + добавкаЧасов;
}

/**
 * Докуда позволено доползти на застое.
 *
 * Берётся первый НЕЗАВЕРШЁННЫЙ этап и половина его остатка. То есть полоса
 * может забраться внутрь того, что сейчас и правда делается, но не дальше
 * середины — и никогда в следующий этап, который ещё не начинался.
 */
function потолокПолзка(): number {
  const w = веса();
  for (const к of Object.keys(w)) {
    const п = пройдено[к] ?? 0;
    if (п < 1) return цель + (1 - п) * w[к] * 0.5;
  }
  return цель;
}

/**
 * Продвинуть показ на заданное время.
 *
 * ⚠️ ВРЕМЯ ДОВОДОМ, А НЕ «ОДИН КАДР», И ЭТО ДВА РАЗНЫХ ПРИОБРЕТЕНИЯ СРАЗУ.
 * Приближение перестаёт зависеть от частоты кадров — на слабой машине полоса
 * идёт так же, как на быстрой. И это же делает показ проверяемым со стенда,
 * где кадров нет вовсе: скрытая панель браузера их не выдаёт.
 */
function продвинуть(dtМс: number, сейчас: number): boolean {
  const прежнее = показано;
  if (цель > показано) {
    показано += (цель - показано) * (1 - Math.exp(-dtМс / ДОГОН_МС));
    if (цель - показано < 0.0004) показано = цель;
  } else if (сейчас - сдвигБыл > ЗАСТОЙ_МС) {
    const потолок = потолокПолзка();
    if (потолок > показано) {
      показано += (потолок - показано) * (1 - Math.exp(-dtМс / ПОЛЗОК_МС));
    }
  }
  if (!финиш && показано > ПОТОЛОК) показано = ПОТОЛОК;
  return Math.abs(показано - прежнее) > 1e-5;
}

/**
 * Перенести `показано` на экран.
 *
 * ⚠️ ПРОВЕРЯЕМ УЗЛЫ, А НЕ `корень`, И ЭТО НУЖНО ДЛЯ ПРОЩАНИЯ. На уходе
 * `корень` обнуляется сразу — чтобы для всех остальных загрузчика уже не
 * было, — а полоса в это время ещё добегает до ста. Сами узлы никуда не
 * делись, и рисовать в них можно.
 */
function применить(): void {
  if (числоЭл === null) return;
  const левее = `${(показано * 100).toFixed(2)}%`;
  if (числоЭл !== null) числоЭл.textContent = String(Math.round(показано * 100));
  if (полоса !== null) полоса.style.width = левее;
  if (поплавок !== null) поплавок.style.left = левее;
  if (кругА !== null) кругА.style.left = левее;
  if (кругБ !== null) кругБ.style.left = левее;
}

let былоМс = 0;

function кадровыйЦикл(): void {
  кадр = requestAnimationFrame(кадровыйЦикл);
  const сейчас = сейчасМс();
  // Первый кадр после показа и возврат из скрытой вкладки дают огромное dt —
  // ограничиваем, иначе полоса прыгнет на весь застой разом.
  const dt = Math.min(100, сейчас - былоМс);
  былоМс = сейчас;
  if (продвинуть(dt, сейчас)) применить();
}

function пуститьЦикл(): void {
  if (кадр !== 0) return;
  былоМс = сейчасМс();
  кадр = requestAnimationFrame(кадровыйЦикл);
}

function остановитьЦикл(): void {
  if (кадр === 0) return;
  cancelAnimationFrame(кадр);
  кадр = 0;
}

/**
 * Прокрутить показ вручную. ТОЛЬКО ДЛЯ СТЕНДА.
 *
 * ⚠️ ЗАВЕДЕНО НЕ РАДИ УДОБСТВА, А ПОТОМУ ЧТО ИНАЧЕ ПЛАВНОСТЬ НЕЧЕМ ПРОВЕРИТЬ.
 * Ведёт показ кадровый цикл, а кадров нет ни при скрытой панели браузера, ни
 * в самом стенде. Здесь тот же самый `продвинуть`, что и в цикле, — стенд
 * проверяет настоящий ход, а не его подобие.
 */
export function прокрутить(мс: number, шагМс = 16): void {
  /*
   * ⚠️ ВРЕМЯ СЧИТАЕМ ОТ НАСТОЯЩЕГО «СЕЙЧАС», А НЕ ОТ ПОРОГА ЗАСТОЯ. Соблазн
   * подставить время, при котором ползок заведомо включён, велик — и стенд
   * тогда проверял бы не то, что работает в игре: задержка перед ползком
   * (ЗАСТОЙ_МС) просто не участвовала бы ни в одной проверке.
   */
  for (let t = 0; t < мс; t += шагМс) {
    const dt = Math.min(шагМс, мс - t);
    добавкаЧасов += dt;
    продвинуть(dt, сейчасМс());
  }
  применить();
}

/**
 * Подпись этапа: самый дальний из НАЧАВШИХСЯ, и только вперёд.
 *
 * ⚠️ РАНЬШЕ ЗДЕСЬ БЫЛО «КТО ПОЗВАЛ ПОСЛЕДНИМ», И ИМЕННО ЭТО ДЁРГАЛО СТРОКУ.
 * Этапы идут не по очереди, а ВПЕРЕМЕШКУ: счётчик байтов шлёт «движок»
 * десятки раз в секунду, пока едут модели снасти, а обход чанков шлёт «берег»
 * шесть раз в секунду. Строка металась между «Разматываю катушку» и «Выбираю
 * место» по нескольку раз за секунду — замерено стендом: сорок смен подписи
 * на сорок вызовов при всего двух разных словах.
 *
 * Теперь подпись — чистая функция состояния, а не следствие порядка вызовов,
 * и защёлкнута на движение только вперёд. Смен получается ровно столько,
 * сколько этапов, и идут они по порядку.
 *
 * ⚠️ «САМЫЙ ДАЛЬНИЙ ИЗ НАЧАВШИХСЯ», А НЕ «ПЕРВЫЙ НЕЗАВЕРШЁННЫЙ». Второе
 * звучит честнее, но на деле означало бы «Разматываю катушку» до самого
 * конца: скачивание идёт фоном и заканчивается позже сборки сцены. Строка
 * должна идти впереди вместе с процентами, а на вопрос «сколько ещё качать»
 * отвечает строка мегабайт под ней — она для того и заведена.
 */
let подписьИндекс = -1;

function обновитьПодпись(): void {
  if (этапЭл === null) return;
  const порядок = Object.keys(веса());
  let дальний = 0;
  for (let i = 0; i < порядок.length; i++) {
    if ((пройдено[порядок[i] as string] ?? 0) > 0) дальний = i;
  }
  if (дальний <= подписьИндекс) return;
  подписьИндекс = дальний;
  const этап = порядок[дальний] as Этап;
  // ⚠️ t() ПРИ ОТСУТСТВИИ КЛЮЧА ВОЗВРАЩАЕТ САМ КЛЮЧ, А НЕ ПУСТО. Поэтому
  // проверка на «||» не спасает: строка «loader.берег» истинна, и игрок
  // увидел бы имя ключа вместо подписи. Ровно это и вышло при первой пробе.
  const ключ = `loader.${этап}`;
  const слово = t(ключ);
  этапЭл.textContent = слово === ключ ? ПОДПИСЬ[этап] : слово;
}

/** Показать загрузчик. Повторный вызов безвреден. */
/**
 * Показывать беды прямо на экране загрузки.
 *
 * ⚠️ ЗАВЕДЕНО ПОТОМУ, ЧТО ЧУЖОЙ ТЕЛЕФОН НЕ РАССПРОСИШЬ. Игра встала на
 * загрузке у одного человека на iPhone: движок скачался, разобрался, памяти
 * вдоволь, WebGL на месте — а мир не собрался. Причина при этом лежала в
 * консоли, до которой с телефона не добраться никак: ни отладчика, ни
 * журнала. Полоса же честно стояла и молчала.
 *
 * ⚠️ ТОЛЬКО ПОКА ЗАГРУЗЧИК НА ЭКРАНЕ. В игре свои ошибки бывают и
 * безобидные — вылезь они поверх воды красной строкой, это была бы уже не
 * помощь, а помеха. Здесь же человек и так ждёт и смотрит в одну точку.
 */
let ловецЗаведён = false;
let бедаЭл: HTMLElement | null = null;

function завестиЛовца(): void {
  if (ловецЗаведён) return;
  ловецЗаведён = true;
  const сказать = (текст: string): void => {
    if (корень === null || текст === "") return;
    if (бедаЭл === null) {
      бедаЭл = document.createElement("div");
      бедаЭл.className = "if-bytes";
      бедаЭл.style.color = "#e0574a";
      бедаЭл.style.maxWidth = "92vw";
      бедаЭл.style.whiteSpace = "pre-wrap";
      корень.appendChild(бедаЭл);
    }
    // Строк держим три: первая беда обычно и есть причина, остальные —
    // её последствия, а длинный список на телефоне не прочитать.
    const было = бедаЭл.textContent ?? "";
    const строки = было === "" ? [] : было.split("\n");
    if (строки.length >= 3) return;
    строки.push(текст.slice(0, 220));
    бедаЭл.textContent = строки.join("\n");
  };
  window.addEventListener("error", (с) => {
    const e = с as ErrorEvent;
    сказать(String(e.message ?? e.error ?? "ошибка"));
  });
  window.addEventListener("unhandledrejection", (с) => {
    const e = с as PromiseRejectionEvent;
    сказать(String(e.reason instanceof Error ? e.reason.message : e.reason));
  });
  /*
   * ⚠️ И ЖАЛОБЫ САМОГО ДВИЖКА ТОЖЕ. Babylon о несобравшемся шейдере не
   * бросает исключение — он пишет в консоль и живёт дальше, а сцена при этом
   * не становится готовой никогда. Через окно такую беду не поймать, только
   * подменой console.error.
   */
  const прежний = console.error.bind(console);
  console.error = (...д: unknown[]): void => {
    прежний(...д);
    сказать(д.map((x) => (x instanceof Error ? x.message : String(x))).join(" "));
  };
}

export function показать(вид: Облик): void {
  завестиЛовца();
  облик = вид;
  for (const к of Object.keys(пройдено)) delete пройдено[к];

  /*
   * ⚠️ ЧИСТИМ ВСЁ СОСТОЯНИЕ ПОКАЗА, А НЕ ОДНО `пройдено`. Здесь и был корень
   * раннего ухода: `просилиУбрать` переживал переход с базы на рыбалку, а
   * `держим` мог унести с собой обещания прошлого показа. Поколение растёт,
   * и всё, что заведено прежним показом, для нового уже не указ.
   */
  поколение += 1;
  if (бедаЭл !== null) {
    бедаЭл.remove();
    бедаЭл = null;
  }
  просилиУбрать = false;
  держим.length = 0;
  цель = 0;
  показано = 0;
  финиш = false;
  подписьИндекс = -1;
  сдвигБыл = сейчасМс();

  if (корень !== null) {
    обновитьПодпись();
    применить();
    пуститьЦикл();
    return;
  }

  /*
   * ⚠️ ПРЕЖНИЙ ЗАГРУЗЧИК СНИМАЕМ СРАЗУ, А НЕ ЖДЁМ, ПОКА ОН ДОГАСНЕТ. Уход
   * растянут на 760 мс (260 до затухания и 500 на само затухание), и всё это
   * время в документе жили ДВА узла с одним и тем же id. Любой
   * querySelector('#if-loader') доставал первый — старый, застывший на ста
   * процентах. На это попалась первая же проба стенда, а до неё — заказ
   * «показать базу поверх рыбалки» рисовал два полноэкранных слоя внахлёст.
   */
  for (const старый of document.querySelectorAll('#if-loader')) старый.remove();

  // Стиль один на все показы: прежде каждый вызов добавлял в head ещё одну
  // копию, и при возвратах в журнал они копились по одной за круг.
  if (document.getElementById('if-loader-style') === null) {
    const стиль = document.createElement('style');
    стиль.id = 'if-loader-style';
    стиль.textContent = СТИЛЬ;
    document.head.appendChild(стиль);
  }

  корень = document.createElement('div');
  корень.id = 'if-loader';
  корень.innerHTML = `
    <div class="if-title">INTERFISHING</div>
    <div class="if-pct"><b>0</b><span>%</span></div>
    <div class="if-line">
      <div class="if-done" style="width:0"></div>
      <div class="if-ring" style="left:0"></div>
      <div class="if-ring if-late" style="left:0"></div>
      <div class="if-float" style="left:0">
        <i class="if-antenna"></i><i class="if-head"></i><i class="if-stripe"></i><i class="if-body"></i><i class="if-keel"></i>
      </div>
    </div>
    <div class="if-stage"></div>
    <!--
      ⚠️ ОТДЕЛЬНАЯ СТРОКА ПОД ЭТАПОМ, А НЕ ВМЕСТО НЕГО. Этап говорит, ЧТО
      сейчас делается («разматываю катушку»), мегабайты — СКОЛЬКО осталось
      ждать. Человеку на медленном канале нужно второе: проценты этапов
      стоят подолгу и выглядят зависанием, а растущие мегабайты видно
      каждую секунду.
    -->
    <div class="if-bytes"></div>
    <div id="if-admin-log" style="display:${_isAdmin ? 'block' : 'none'}; max-width:600px; width:80%; height:110px; overflow:hidden; font-family:monospace; font-size:10px; color:rgba(255,255,255,0.4); text-align:left; margin-top:20px; line-height:1.2; word-break:break-all;"></div>
  `;
  document.body.appendChild(корень);

  полоса = корень.querySelector('.if-done');
  поплавок = корень.querySelector('.if-float');
  const кольца = корень.querySelectorAll('.if-ring');
  кругА = кольца[0] as HTMLElement;
  кругБ = кольца[1] as HTMLElement;
  числоЭл = корень.querySelector('.if-pct b');
  этапЭл = корень.querySelector('.if-stage');
  байтыЭл = корень.querySelector('.if-bytes');
  adminLogЭл = корень.querySelector('#if-admin-log');

  применить();
  обновитьПодпись();
  пуститьЦикл();
  завестиСторожа();
}

/**
 * Отметить продвижение по этапу.
 *
 * @param этап   какой из пяти
 * @param часть  0..1 внутри этапа
 */
export function шаг(этап: Этап, часть = 1): void {
  const было = пройдено[этап] ?? 0;
  const стало = Math.max(было, Math.max(0, Math.min(1, часть)));

  /*
   * ⚠️ ХОЛОСТОЙ ВЫЗОВ НЕ ДОЛЖЕН ТРОГАТЬ ЭКРАН ВООБЩЕ. Прежде `шаг` при любом
   * вызове переписывал подпись — даже когда доля не менялась, потому что
   * Math.max её удержал. А таких вызовов большинство: счётчик байтов шлёт
   * «движок» десятки раз в секунду, и пока его доля не перебила пол, каждый
   * из них был чистой перерисовкой подписи. Стенд ловит это отдельной
   * проверкой: шаг назад по этапу менял слово, не меняя числа.
   */
  if (стало === было) return;
  пройдено[этап] = стало;
  if (корень === null) return;

  const d = доля();
  if (d > цель) {
    цель = d;
    сдвигБыл = сейчасМс();
    завестиСторожа();
  }
  обновитьПодпись();
}

/**
 * Показать, что качается ПРЯМО СЕЙЧАС: имя файла и его вес.
 *
 * ⚠️ ОДНА СТРОКА, НО ЧЕРЕЗ НЕЁ ПРОХОДЯТ ВСЕ ФАЙЛЫ ПО ОЧЕРЕДИ. Смысл её не в
 * том, чтобы сообщить итог, а в том, чтобы было видно РАБОТУ: имя сменилось —
 * значит дело идёт. Общая сумма «32.8 из 49.5 МБ» на медленном канале
 * прибавляет по десятой доле в секунду и глазом неотличима от замершей.
 *
 * ⚠️ НИКАКИХ ПРОЦЕНТОВ. Доля отдельного файла нам подконтрольна не всегда:
 * байты в полёте видно только у того, что грузится через XMLHttpRequest, а
 * картинки браузер тянет сам и сообщает о них лишь по завершении. Строка, где
 * у одних файлов проценты бегут, а у других прыгают с нуля на сто, врёт хуже,
 * чем строка без процентов вовсе. Имя и вес известны точно — их и пишем.
 *
 * ⚠️ ИМЯ ФАЙЛА КАК ЕСТЬ, БЕЗ ПУТИ И БЕЗ ПРИДУМАННЫХ НАЗВАНИЙ. С «reel_body.glb»
 * можно пойти в панель разработчика, в журнал сервера, в письмо о поломке.
 * С «катушкой» — некуда.
 *
 * ⚠️ ПУСТОЕ ИМЯ ГАСИТ СТРОКУ. Скачивание кончилось, а загрузчик ещё стоит —
 * строятся чанки, собираются шейдеры. Оставь мы последний файл на экране,
 * человек читал бы это как «всё ещё качается».
 */
export function файл(имя: string, размер: number): void {
  if (байтыЭл === null) return;
  байтыЭл.textContent =
    имя === '' ? '' : размер > 0 ? t('load.file_line', { name: имя, size: мб(размер) }) : имя;
}

/*
 * ── Сторож ────────────────────────────────────────────────────────────────
 *
 * ⚠️ ЗАПАС ВЗЯТ ЩЕДРЫЙ НАМЕРЕННО, И ЭТО НЕ ПЕРЕСТРАХОВКА. Цель может честно
 * не двигаться ОЧЕНЬ долго: пока едет один файл движка на восемь мегабайт,
 * событий нет ни одного, а на медленном канале это минуты. Поставь мы срок
 * покороче — сторож кричал бы «беда» ровно тому, у кого всё идёт, просто
 * медленно, а это худший из возможных обманов.
 *
 * ⚠️ И ОН НИЧЕГО НЕ ОСТАНАВЛИВАЕТ. Он лишь добавляет строку и выход: загрузка
 * продолжается, и если она всё-таки дойдёт — человек просто войдёт в игру.
 * Право решать, ждать дальше или вернуться, остаётся за ним.
 */
const СТОРОЖ_МС = 180000;
let сторож = 0;

function снятьСторожа(): void {
  if (сторож === 0) return;
  clearTimeout(сторож);
  сторож = 0;
}

function завестиСторожа(): void {
  снятьСторожа();
  сторож = window.setTimeout(() => {
    сторож = 0;
    if (корень === null) return;
    датьВыход(t('load.stalled'));
  }, СТОРОЖ_МС);
}

/** Добавить строку и кнопку возврата. Само по себе ничего не прерывает. */
function датьВыход(подсказка?: string): void {
  if (корень === null || корень.querySelector('.if-back') !== null) return;
  if (подсказка !== undefined) {
    const строка = document.createElement('div');
    строка.className = 'if-bytes';
    строка.textContent = подсказка;
    корень.appendChild(строка);
  }
  const кнопка = document.createElement('button');
  кнопка.className = 'if-back';
  кнопка.textContent = t('load.back_to_lobby');
  кнопка.addEventListener('click', () => {
    location.reload();
  });
  корень.appendChild(кнопка);
}

/**
 * Загрузка не удалась: сказать словами и дать выход.
 *
 * ⚠️ ЗАВЕДЕНО ПРОТИВ ВЕЧНОГО ЭКРАНА ЗАГРУЗКИ, И ЭТО НЕ УКРАШЕНИЕ. Отказ при
 * открытии водоёма оставлял игрока на трети полосы навсегда: этап не
 * отмечался, чанки без водоёма не строились, условие ухода не выполнялось
 * никогда, а сам отказ уходил в необработанное обещание — ни строки на
 * экране, ни звука. Молчащая поломка хуже громкой: человек ждёт и винит
 * свой интернет.
 *
 * ⚠️ ВЫХОД — ПЕРЕЗАГРУЗКА СТРАНИЦЫ, А НЕ ВОЗВРАТ В ЖУРНАЛ ВЫЗОВОМ. Движок к
 * этому мигу уже поднят, сцена собрана наполовину, и разбирать всё это назад
 * значит держать путь отката, которым пользуются раз в год и который потому
 * всегда сломан. Ключ входа лежит в localStorage — после перезагрузки человек
 * снова окажется на базе, не набирая пароля.
 */
export function сбой(текст: string): void {
  остановитьЦикл();
  снятьСторожа();
  if (этапЭл !== null) {
    этапЭл.textContent = текст;
    этапЭл.classList.add('if-bad');
  }
  if (байтыЭл !== null) байтыЭл.textContent = '';
  датьВыход();
}

/** Мегабайты одним знаком после запятой: «12.4». */
function мб(байт: number): string {
  return (байт / 1048576).toFixed(1);
}

/**
 * Не гасить загрузчик, пока это обещание не исполнится.
 *
 * ⚠️ ЗАВЕДЕНО РАДИ ПУСТОГО СПИННИНГА, И ЭТО НЕ КРАСОТА. Модели снасти —
 * рукоять и три части катушки — грузятся асинхронно, а загрузчик уходил по
 * готовности ЗЕМЛИ. Игрок оказывался на берегу с белой нетекстурированной
 * катушкой в руках и видел, как она «дособирается» у него на глазах.
 *
 * ⚠️ ОТКАЗ ТОЖЕ СЧИТАЕТСЯ ИСПОЛНЕНИЕМ. Не приди один файл вовсе — загрузчик
 * обязан уйти: лучше войти в игру без части снасти, чем остаться на экране
 * загрузки навсегда.
 */
const держим: Promise<unknown>[] = [];
let просилиУбрать = false;

export function держать(обещание: Promise<unknown>): void {
  /*
   * ⚠️ ЗАПОМИНАЕМ НОМЕР ПОКАЗА. Обещание может исполниться уже после того,
   * как загрузчик показан заново под другим обликом; тогда оно не имеет к
   * происходящему никакого отношения и решать судьбу нового показа не должно.
   */
  const моё = поколение;
  держим.push(обещание);
  void Promise.resolve(обещание)
    .catch(() => undefined)
    .then(() => {
      if (моё !== поколение) return;
      const i = держим.indexOf(обещание);
      if (i >= 0) держим.splice(i, 1);
      if (просилиУбрать && держим.length === 0) убратьТеперь();
    });
}

/**
 * Убрать загрузчик.
 *
 * ⚠️ СНАЧАЛА ДОВОДИМ ДО СТА, ПОТОМ ГАСИМ. Полоска, исчезнувшая на
 * восьмидесяти семи процентах, читается как обрыв, даже когда всё хорошо.
 */
/**
 * Обещание, которое сбудется, когда загрузчик действительно уйдёт с экрана.
 *
 * ⚠️ НУЖНО ТОМУ, ЧТО ПОКАЗЫВАЮТ ВМЕСТЕ С ИГРОЙ. Приборы рыболова поднимались
 * в тот миг, когда пускался кадровый цикл, — а цикл пускается РАНЬШЕ, чем мир
 * готов: чанкам нужны кадры, чтобы строиться, шейдерам — чтобы собраться.
 * Выходило, что поверх загрузчика уже висят приборы, а под ним пусто; игрок
 * видел их и ждал игру ещё десяток секунд.
 *
 * ⚠️ ЕСЛИ ЗАГРУЗЧИКА НЕТ — СБЫВАЕТСЯ СРАЗУ. Иначе пути, где его не
 * показывают вовсе (админ рисует карту, возврат в уже готовый мир), остались
 * бы без приборов навсегда.
 */
export function готовность(): Promise<void> {
  if (корень === null && ждущие.length === 0) return Promise.resolve();
  return new Promise<void>((сбылось) => {
    ждущие.push(сбылось);
  });
}

/** Кому сообщить, что загрузчик ушёл. */
const ждущие: (() => void)[] = [];

export function убрать(): void {
  просилиУбрать = true;
  // Кто-то ещё не догрузился — уйдём, когда он закончит (см. держать).
  if (держим.length > 0) return;
  убратьТеперь();
}

function убратьТеперь(): void {
  if (корень === null) return;
  for (const к of Object.keys(веса())) пройдено[к] = 1;
  /*
   * ⚠️ ДО СТА ПОЛОСА ДОБЕГАЕТ, А НЕ ПРЫГАЕТ. Прежде здесь стояло присваивание
   * ста процентов разом, и последним, что видел игрок, всегда был скачок —
   * с восьмидесяти с чем-то сразу на сто. Теперь уход только двигает цель, а
   * доводит показ тот же кадровый цикл, что вёл его всю дорогу.
   */
  цель = 1;
  финиш = true;
  сдвигБыл = сейчасМс();
  снятьСторожа();
  обновитьПодпись();

  const это = корень;
  корень = null;
  // Ждущие узнают об этом ровно здесь — в единственном месте, где загрузчик
  // и правда уходит. Ждать добега нельзя: приборы поднимаются по этому же
  // обещанию, и лишняя треть секунды была бы видна.
  const кому = ждущие.splice(0, ждущие.length);
  for (const позвать of кому) позвать();

  setTimeout(() => {
    остановитьЦикл();
    // Ровно сто на прощание: округление не должно оставить «99».
    показано = 1;
    применить();
    это.classList.add('if-gone');
    setTimeout(() => it_remove(это), 500);
  }, ДОБЕГ_МС);
}

function it_remove(e: HTMLElement): void {
  if (e.parentNode !== null) e.parentNode.removeChild(e);
}
